import { defineStore } from 'pinia'
import { store } from '../index'
import type { DictDataVO } from '@/api/system/dict/dict.data'
import type { ElementPlusInfoType } from '@/types/elementPlus'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'
const { wsCache } = useCache('sessionStorage')
import { getSimpleDictDataList } from '@/api/system/dict/dict.data'

export interface DictValueType {
  dictType: string
  value: any
  label: string
  colorType: ElementPlusInfoType | ''
  cssClass: string
}
export interface DictTypeType {
  dictType: string
  dictValue: DictValueType[]
}
export interface DictState {
  dictMap: Record<string, DictValueType[]>
  isSetDict: boolean
}

let dictLoadPromise: Promise<DictDataVO[]> | undefined

const buildDictMap = (dictDataList: DictDataVO[]): Record<string, DictValueType[]> => {
  return dictDataList.reduce<Record<string, DictValueType[]>>((dictMap, dictData) => {
    const dictValues = dictMap[dictData.dictType] || (dictMap[dictData.dictType] = [])
    dictValues.push({
      dictType: dictData.dictType,
      value: dictData.value,
      label: dictData.label,
      colorType: dictData.colorType as ElementPlusInfoType | '',
      cssClass: dictData.cssClass
    })
    return dictMap
  }, {})
}

export const useDictStore = defineStore('dict', {
  state: (): DictState => ({
    dictMap: {},
    isSetDict: false
  }),
  getters: {
    getDictMap(): Recordable {
      return this.dictMap
    },
    getIsSetDict(): boolean {
      return this.isSetDict
    }
  },
  actions: {
    async setDictMap() {
      if (this.isSetDict) {
        return
      }
      const dictMap = wsCache.get(CACHE_KEY.DICT_CACHE)
      if (dictMap) {
        this.dictMap = dictMap
        this.isSetDict = true
        return
      }
      if (!dictLoadPromise) {
        dictLoadPromise = getSimpleDictDataList().finally(() => {
          dictLoadPromise = undefined
        })
      }
      const dictDataMap = buildDictMap(await dictLoadPromise)
      this.dictMap = dictDataMap
      this.isSetDict = true
      wsCache.set(CACHE_KEY.DICT_CACHE, dictDataMap, { exp: 60 }) // 60 秒过期
    },
    getDictByType(type: string) {
      return this.dictMap[type] || []
    },
    async resetDict() {
      wsCache.delete(CACHE_KEY.DICT_CACHE)
      this.dictMap = {}
      this.isSetDict = false
      await this.setDictMap()
    }
  }
})

export const useDictStoreWithOut = () => {
  return useDictStore(store)
}
